"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSidebarStore } from "@/stores/sidebarStore";
import { useArticleStore } from "@/stores/articleStore";
import { useBlogPostStore } from "@/stores/blogPostStore";
import {
  SidebarWithDetails,
  ArticleDetails,
  BlogPostDetails,
  UpdateSidebarDto,
} from "@/types/sidebar";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

import {
  RefreshCw,
  Save,
  Plus,
  X,
  FileText,
  Search,
  GripVertical,
  Film,
} from "lucide-react";

import { showToast } from "@/lib/toast";
import { useDebounce } from "@/hooks/useDebounce";
import InfiniteScroll from "@/components/ui/infinite-scroll";
import DotsLoading from "@/components/ui/dots-loading";

interface SortableArticleItemProps {
  article: ArticleDetails;
  onRemove: () => void;
  sectionKey: string;
}

function SortableArticleItem({
  article,
  onRemove,
  sectionKey,
}: SortableArticleItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `${sectionKey}-${article._id}`,
    data: { section: sectionKey, articleId: article._id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
          title="Kéo để sắp xếp"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>

        <div className="flex-shrink-0">
          {article.image?.url ? (
            <img
              src={article.image.url}
              alt={article.title}
              className="w-12 h-16 bg-gray-100 rounded object-cover"
            />
          ) : (
            <div className="w-12 h-16 bg-gray-100 rounded flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {article.title}
          </h4>
          <p className="text-xs text-gray-500">/{article.slug}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
          title="Xóa khỏi danh sách"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

interface ArticleSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (articles: ArticleDetails[]) => void;
  title: string;
}

function ArticleSelector({
  isOpen,
  onClose,
  onSelect,
  title,
}: ArticleSelectorProps) {
  const [selectedArticles, setSelectedArticles] = useState<ArticleDetails[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { articles, isLoading, fetchArticles, error, pagination } =
    useArticleStore();

  const [allLoadedArticles, setAllLoadedArticles] = useState<ArticleDetails[]>(
    []
  );

  const adaptedArticles = useMemo(() => {
    return articles.map(
      (article): ArticleDetails => ({
        _id: article._id,
        title: article.title || "",
        slug: article.slug,
        image: article.featuredImage
          ? {
              url: article.featuredImage.url,
              alt: article.featuredImage.alt,
              title: article.featuredImage.title,
            }
          : undefined,
      })
    );
  }, [articles]);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setHasMore(true);
      setAllLoadedArticles([]);
      fetchArticles({
        page: 1,
        limit: 20,
        search: debouncedSearchTerm || undefined,
      });
    }
  }, [isOpen, debouncedSearchTerm, fetchArticles]);

  useEffect(() => {
    if (currentPage === 1) {
      setAllLoadedArticles(adaptedArticles);
    } else {
      setAllLoadedArticles((prev) => {
        const existingIds = new Set(prev.map((art) => art._id));
        const newArticles = adaptedArticles.filter(
          (art) => !existingIds.has(art._id)
        );
        return [...prev, ...newArticles];
      });
    }

    if (pagination) {
      const newHasMore = currentPage < pagination.totalPages;
      setHasMore(newHasMore);
    }
  }, [adaptedArticles, currentPage, pagination]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) {
      return;
    }

    const nextPage = currentPage + 1;

    fetchArticles({
      page: nextPage,
      limit: 20,
      search: debouncedSearchTerm || undefined,
    }).then(() => {
      setCurrentPage(nextPage);
      const newHasMore = nextPage < (pagination?.totalPages || 0);
      setHasMore(newHasMore);
    });
  }, [
    currentPage,
    hasMore,
    isLoading,
    debouncedSearchTerm,
    fetchArticles,
    pagination,
  ]);

  const filteredArticles = useMemo(() => {
    return allLoadedArticles;
  }, [allLoadedArticles]);

  const handleArticleSelect = useCallback((article: ArticleDetails) => {
    setSelectedArticles((prev) => {
      const isSelected = prev.some((art) => art._id === article._id);
      if (isSelected) {
        return prev.filter((art) => art._id !== article._id);
      } else {
        return [...prev, article];
      }
    });
  }, []);

  const handleConfirm = () => {
    onSelect(selectedArticles);
    setSelectedArticles([]);
    setSearchTerm("");
    setCurrentPage(1);
    setHasMore(true);
    setAllLoadedArticles([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedArticles([]);
    setSearchTerm("");
    setCurrentPage(1);
    setHasMore(true);
    setAllLoadedArticles([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Chọn bài viết để thêm vào danh sách
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Tìm kiếm bài viết theo tiêu đề hoặc slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex-1 min-h-0">
          {error ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-red-600 text-lg font-medium mb-2">
                  Lỗi khi tải bài viết
                </div>
                <div className="text-gray-500 mb-4">{error}</div>
                <Button
                  onClick={() => fetchArticles({ page: 1, limit: 20 })}
                  variant="outline"
                  size="sm"
                >
                  Thử lại
                </Button>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto max-h-[300px]">
              {filteredArticles.length === 0 ? (
                isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <DotsLoading
                      size="lg"
                      color="#3b82f6"
                      text="Đang tải danh sách bài viết..."
                    />
                  </div>
                ) : debouncedSearchTerm ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Không tìm thấy bài viết nào với từ khóa "
                        {debouncedSearchTerm}"
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Không có bài viết nào
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <div className="space-y-2 p-2">
                  <InfiniteScroll
                    isLoading={isLoading}
                    hasMore={hasMore}
                    next={loadMore}
                    threshold={0.5}
                    rootMargin="100px"
                  >
                    {filteredArticles.map((article) => {
                      const isSelected = selectedArticles.some(
                        (art) => art._id === article._id
                      );
                      return (
                        <div
                          key={article._id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                            isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => handleArticleSelect(article)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {article.image?.url ? (
                                <img
                                  src={article.image.url}
                                  alt={article.title}
                                  className="w-12 h-16 bg-gray-100 rounded object-cover"
                                />
                              ) : (
                                <div className="w-12 h-16 bg-gray-100 rounded flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {article.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                /{article.slug}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {isLoading && hasMore && (
                      <div className="flex justify-center py-3 border-t border-gray-100 mt-2">
                        <DotsLoading
                          size="xs"
                          color="#9ca3af"
                          text="Đang tải thêm..."
                        />
                      </div>
                    )}
                  </InfiniteScroll>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="text-sm text-gray-500">
            Đã chọn: {selectedArticles.length} bài viết | Đã tải:{" "}
            {filteredArticles.length}/{pagination?.total || 0} bài viết
            {searchTerm !== debouncedSearchTerm && (
              <span className="ml-2 text-blue-600">
                <RefreshCw className="w-3 h-3 inline animate-spin mr-1" />
                Đang tìm kiếm...
              </span>
            )}
            {hasMore && (
              <span className="ml-2 text-green-600">
                (Còn {(pagination?.total || 0) - filteredArticles.length} bài viết -
                Cuộn để tải thêm)
              </span>
            )}
            {!hasMore && filteredArticles.length > 0 && (
              <span className="ml-2 text-gray-400">
                (Đã tải hết tất cả bài viết)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Hủy
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedArticles.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Thêm {selectedArticles.length} bài viết
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BlogPostSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (posts: BlogPostDetails[]) => void;
  title: string;
}

function BlogPostSelector({
  isOpen,
  onClose,
  onSelect,
  title,
}: BlogPostSelectorProps) {
  const [selectedPosts, setSelectedPosts] = useState<BlogPostDetails[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { blogPosts, isLoading, fetchBlogPosts, error, pagination } =
    useBlogPostStore();

  const [allLoadedPosts, setAllLoadedPosts] = useState<BlogPostDetails[]>([]);

  const adaptedPosts = useMemo(() => {
    return blogPosts.map(
      (post): BlogPostDetails => ({
        _id: post._id,
        title: post.title || "",
        slug: post.slug,
        image: post.featuredImage
          ? {
              url: post.featuredImage.url,
              alt: post.featuredImage.alt,
              title: post.featuredImage.title,
            }
          : undefined,
        category: post.category,
      })
    );
  }, [blogPosts]);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setHasMore(true);
      setAllLoadedPosts([]);
      fetchBlogPosts({
        page: 1,
        limit: 20,
        search: debouncedSearchTerm || undefined,
      });
    }
  }, [isOpen, debouncedSearchTerm, fetchBlogPosts]);

  useEffect(() => {
    if (currentPage === 1) {
      setAllLoadedPosts(adaptedPosts);
    } else {
      setAllLoadedPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p._id));
        const newPosts = adaptedPosts.filter((p) => !existingIds.has(p._id));
        return [...prev, ...newPosts];
      });
    }

    if (pagination) {
      const newHasMore = currentPage < pagination.totalPages;
      setHasMore(newHasMore);
    }
  }, [adaptedPosts, currentPage, pagination]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) {
      return;
    }

    const nextPage = currentPage + 1;

    fetchBlogPosts({
      page: nextPage,
      limit: 20,
      search: debouncedSearchTerm || undefined,
    }).then(() => {
      setCurrentPage(nextPage);
      const newHasMore = nextPage < (pagination?.totalPages || 0);
      setHasMore(newHasMore);
    });
  }, [
    currentPage,
    hasMore,
    isLoading,
    debouncedSearchTerm,
    fetchBlogPosts,
    pagination,
  ]);

  const filteredPosts = useMemo(() => {
    return allLoadedPosts;
  }, [allLoadedPosts]);

  const handlePostSelect = useCallback((post: BlogPostDetails) => {
    setSelectedPosts((prev) => {
      const isSelected = prev.some((p) => p._id === post._id);
      if (isSelected) {
        return prev.filter((p) => p._id !== post._id);
      } else {
        return [...prev, post];
      }
    });
  }, []);

  const handleConfirm = () => {
    onSelect(selectedPosts);
    setSelectedPosts([]);
    setSearchTerm("");
    setCurrentPage(1);
    setHasMore(true);
    setAllLoadedPosts([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedPosts([]);
    setSearchTerm("");
    setCurrentPage(1);
    setHasMore(true);
    setAllLoadedPosts([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Chọn bài viết blog để thêm vào danh sách
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Tìm kiếm bài viết theo tiêu đề hoặc slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex-1 min-h-0">
          {error ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-red-600 text-lg font-medium mb-2">
                  Lỗi khi tải bài viết
                </div>
                <div className="text-gray-500 mb-4">{error}</div>
                <Button
                  onClick={() => fetchBlogPosts({ page: 1, limit: 20 })}
                  variant="outline"
                  size="sm"
                >
                  Thử lại
                </Button>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto max-h-[300px]">
              {filteredPosts.length === 0 ? (
                isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <DotsLoading
                      size="lg"
                      color="#3b82f6"
                      text="Đang tải danh sách bài viết..."
                    />
                  </div>
                ) : debouncedSearchTerm ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Không tìm thấy bài viết nào với từ khóa "
                        {debouncedSearchTerm}"
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Không có bài viết nào
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <div className="space-y-2 p-2">
                  <InfiniteScroll
                    isLoading={isLoading}
                    hasMore={hasMore}
                    next={loadMore}
                    threshold={0.5}
                    rootMargin="100px"
                  >
                    {filteredPosts.map((post) => {
                      const isSelected = selectedPosts.some(
                        (p) => p._id === post._id
                      );
                      return (
                        <div
                          key={post._id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                            isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => handlePostSelect(post)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {post.image?.url ? (
                                <img
                                  src={post.image.url}
                                  alt={post.title}
                                  className="w-12 h-16 bg-gray-100 rounded object-cover"
                                />
                              ) : (
                                <div className="w-12 h-16 bg-gray-100 rounded flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {post.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                /{post.slug}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {isLoading && hasMore && (
                      <div className="flex justify-center py-3 border-t border-gray-100 mt-2">
                        <DotsLoading
                          size="xs"
                          color="#9ca3af"
                          text="Đang tải thêm..."
                        />
                      </div>
                    )}
                  </InfiniteScroll>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="text-sm text-gray-500">
            Đã chọn: {selectedPosts.length} bài viết | Đã tải:{" "}
            {filteredPosts.length}/{pagination?.total || 0} bài viết
            {searchTerm !== debouncedSearchTerm && (
              <span className="ml-2 text-blue-600">
                <RefreshCw className="w-3 h-3 inline animate-spin mr-1" />
                Đang tìm kiếm...
              </span>
            )}
            {hasMore && (
              <span className="ml-2 text-green-600">
                (Còn {(pagination?.total || 0) - filteredPosts.length} bài viết -
                Cuộn để tải thêm)
              </span>
            )}
            {!hasMore && filteredPosts.length > 0 && (
              <span className="ml-2 text-gray-400">
                (Đã tải hết tất cả bài viết)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Hủy
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedPosts.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Thêm {selectedPosts.length} bài viết
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SidebarPage() {
  const { sidebar, isLoading, isUpdating, error, fetchSidebar, updateSidebar } =
    useSidebarStore();

  const [editedSidebar, setEditedSidebar] = useState<SidebarWithDetails | null>(
    null
  );
  const [articleSelectorOpen, setArticleSelectorOpen] = useState(false);
  const [blogPostSelectorOpen, setBlogPostSelectorOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchSidebar();
  }, [fetchSidebar]);

  useEffect(() => {
    if (sidebar) {
      // Ensure top10films and posts are always arrays (handle old data structure)
      setEditedSidebar({
        ...sidebar,
        top10films: sidebar.top10films || [],
        posts: sidebar.posts || [],
      });
    }
  }, [sidebar]);

  const handleSave = async () => {
    if (!editedSidebar) return;

    const updateDto: UpdateSidebarDto = {
      top10films: (editedSidebar.top10films || []).map((m) => m._id),
      posts: (editedSidebar.posts || []).map((a) => a._id),
    };

    try {
      await updateSidebar(updateDto);
      showToast.success("Cập nhật sidebar thành công");
    } catch {
      // Error is handled in store
    }
  };

  const handleRemoveItem = (
    section: "top10films" | "posts",
    itemId: string
  ) => {
    if (!editedSidebar) return;

    setEditedSidebar({
      ...editedSidebar,
      [section]: (editedSidebar[section] as any[]).filter(
        (item) => item._id !== itemId
      ),
    });
  };

  const handleAddFilms = (items: ArticleDetails[]) => {
    if (!editedSidebar) return;

    const currentItems = editedSidebar.top10films || [];
    const existingIds = new Set(currentItems.map((item) => item._id));

    const newItems = items.filter((item) => !existingIds.has(item._id));

    if (newItems.length === 0) {
      showToast.warning("Tất cả phim đã tồn tại trong danh sách");
      return;
    }

    if (newItems.length < items.length) {
      showToast.info(
        `Đã thêm ${newItems.length}/${items.length} phim (${
          items.length - newItems.length
        } phim đã tồn tại)`
      );
    }

    setEditedSidebar({
      ...editedSidebar,
      top10films: [...currentItems, ...newItems],
    });
  };

  const handleAddPosts = (items: BlogPostDetails[]) => {
    if (!editedSidebar) return;

    const currentItems = editedSidebar.posts || [];
    const existingIds = new Set(currentItems.map((item) => item._id));

    const newItems = items.filter((item) => !existingIds.has(item._id));

    if (newItems.length === 0) {
      showToast.warning("Tất cả bài viết đã tồn tại trong danh sách");
      return;
    }

    if (newItems.length < items.length) {
      showToast.info(
        `Đã thêm ${newItems.length}/${items.length} bài viết (${
          items.length - newItems.length
        } bài viết đã tồn tại)`
      );
    }

    setEditedSidebar({
      ...editedSidebar,
      posts: [...currentItems, ...newItems],
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (!editedSidebar) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!activeData?.section || !overData?.section) return;

    if (activeData.section !== overData.section) return;

    const sourceSection = activeData.section as "top10films" | "posts";

    const activeItemId = activeData.articleId;
    const overItemId = overData.articleId;

    if (!activeItemId || !overItemId) return;

    const sectionItems = editedSidebar[sourceSection] as ArticleDetails[];
    const oldIndex = sectionItems.findIndex(
      (item) => item._id === activeItemId
    );
    const newIndex = sectionItems.findIndex((item) => item._id === overItemId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(sectionItems, oldIndex, newIndex);

    setEditedSidebar({
      ...editedSidebar,
      [sourceSection]: newItems,
    });
  };


  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-500">Đang tải sidebar...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium mb-2">
              Lỗi khi tải sidebar
            </div>
            <div className="text-gray-500 mb-4">{error}</div>
            <Button onClick={() => fetchSidebar()}>Thử lại</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý Sidebar
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Quản lý nội dung hiển thị trên sidebar
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  Cấu hình
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => fetchSidebar()}
                variant="outline"
                disabled={isLoading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Làm mới
              </Button>
              <Button
                onClick={handleSave}
                disabled={!editedSidebar || isUpdating}
              >
                <Save className="w-4 h-4 mr-2" />
                {isUpdating ? "Đang lưu..." : "Lưu thay đổi"}
              </Button>
            </div>
          </div>

          {editedSidebar && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top 10 Films */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Film className="w-5 h-5 text-blue-600" />
                      Top 10 Phim
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {editedSidebar.top10films?.length || 0} phim
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setArticleSelectorOpen(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Thêm phim
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SortableContext
                    items={(editedSidebar.top10films || []).map(
                      (item) => `top10films-${item._id}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {(editedSidebar.top10films || []).map((item) => (
                        <SortableArticleItem
                          key={item._id}
                          article={item}
                          sectionKey="top10films"
                          onRemove={() =>
                            handleRemoveItem("top10films", item._id)
                          }
                        />
                      ))}
                      {(editedSidebar.top10films?.length || 0) === 0 && (
                        <div className="text-center py-6 text-gray-500">
                          <Film className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Chưa có phim nào</p>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </CardContent>
              </Card>

              {/* Posts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      Bài viết
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {editedSidebar.posts?.length || 0} bài viết
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBlogPostSelectorOpen(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Thêm bài viết
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SortableContext
                    items={(editedSidebar.posts || []).map(
                      (item) => `posts-${item._id}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {(editedSidebar.posts || []).map((item) => (
                        <SortableArticleItem
                          key={item._id}
                          article={item}
                          sectionKey="posts"
                          onRemove={() => handleRemoveItem("posts", item._id)}
                        />
                      ))}
                      {(editedSidebar.posts?.length || 0) === 0 && (
                        <div className="text-center py-6 text-gray-500">
                          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Chưa có bài viết nào</p>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Article Selector for Top 10 Films */}
          <ArticleSelector
            isOpen={articleSelectorOpen}
            onClose={() => setArticleSelectorOpen(false)}
            onSelect={(articles) => handleAddFilms(articles)}
            title="Thêm phim vào Top 10"
          />

          {/* BlogPost Selector for Posts */}
          <BlogPostSelector
            isOpen={blogPostSelectorOpen}
            onClose={() => setBlogPostSelectorOpen(false)}
            onSelect={(posts) => handleAddPosts(posts)}
            title="Thêm bài viết vào Sidebar"
          />
        </div>
        <DragOverlay />
      </DndContext>
    </DashboardLayout>
  );
}
